import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Radio, FileCode, Send, RefreshCw, Search } from "lucide-react";
import {
  useSngpcConfig, useSngpcTransmissoes, obterXmlTransmissao,
} from "@/features/anvisa/hooks/useSngpcTransmissao";

function primeiroDiaMes(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function hojeIso(): string { return new Date().toISOString().slice(0, 10); }

function statusBadge(status: string) {
  const map: Record<string, string> = {
    "Gerado": "bg-slate-100 text-slate-700",
    "Enviado": "bg-blue-100 text-blue-700",
    "Validado": "bg-emerald-100 text-emerald-700",
    "Erro": "bg-red-100 text-red-700",
    "Reprocessar": "bg-amber-100 text-amber-700",
  };
  return <Badge className={`${map[status] ?? "bg-muted"} hover:${map[status] ?? ""}`}>{status}</Badge>;
}

export default function TransmissoesSngpc() {
  const { config, isLoading: cfgLoading, salvarConfig, salvando } = useSngpcConfig();
  const { transmissoes, isLoading, enviarInventario, enviarMovimentacao, reenviar, consultarStatus, enviando } =
    useSngpcTransmissoes();

  // Estado da config (inicializa do servidor sob demanda).
  const [cfgForm, setCfgForm] = useState({
    ativo: false, email: "", senha: "", cpfTransmissor: "", usarApiNova: true, _init: false,
  });
  if (config && !cfgForm._init) {
    setCfgForm({
      ativo: config.ativo, email: config.email ?? "", senha: "",
      cpfTransmissor: config.cpfTransmissor ?? "", usarApiNova: config.usarApiNova, _init: true,
    });
  }

  const [inicio, setInicio] = useState(primeiroDiaMes());
  const [fim, setFim] = useState(hojeIso());
  const [dataRef, setDataRef] = useState(hojeIso());

  const [xmlAberto, setXmlAberto] = useState<{ id: number; xml: string } | null>(null);

  const verXml = async (id: number) => {
    try { setXmlAberto({ id, xml: await obterXmlTransmissao(id) }); }
    catch { setXmlAberto({ id, xml: "Falha ao carregar XML." }); }
  };

  const salvar = () => salvarConfig({
    ativo: cfgForm.ativo, email: cfgForm.email, senha: cfgForm.senha || null,
    cpfTransmissor: cfgForm.cpfTransmissor, usarApiNova: cfgForm.usarApiNova,
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Radio className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Transmissões SNGPC</h1>
          <p className="text-muted-foreground">Escrituração ANVISA de medicamentos controlados — geração, envio e acompanhamento.</p>
        </div>
      </div>

      {/* Configuração */}
      <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-4">
        <h3 className="text-base font-semibold">Configuração</h3>
        {cfgLoading ? <p className="text-sm text-muted-foreground">Carregando...</p> : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1">
                <Label>E-mail (portal ANVISA)</Label>
                <Input value={cfgForm.email} onChange={(e) => setCfgForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Senha {config?.temSenha && <span className="text-xs text-muted-foreground">(definida)</span>}</Label>
                <Input type="password" placeholder={config?.temSenha ? "•••••• (manter)" : ""}
                  value={cfgForm.senha} onChange={(e) => setCfgForm(p => ({ ...p, senha: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>CPF do transmissor</Label>
                <Input maxLength={11} placeholder="11 dígitos" value={cfgForm.cpfTransmissor}
                  onChange={(e) => setCfgForm(p => ({ ...p, cpfTransmissor: e.target.value.replace(/\D/g, "") }))} />
              </div>
              <div className="space-y-1">
                <Label>Canal</Label>
                <Select value={cfgForm.usarApiNova ? "1" : "2"} onValueChange={(v) => setCfgForm(p => ({ ...p, usarApiNova: v === "1" }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">API REST (recomendado)</SelectItem>
                    <SelectItem value="2">SOAP (legado)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch checked={cfgForm.ativo} onCheckedChange={(v) => setCfgForm(p => ({ ...p, ativo: v }))} />
                <Label>SNGPC ativo</Label>
              </div>
              <Button type="button" onClick={salvar} disabled={salvando}>
                {salvando ? "Salvando..." : "Salvar configuração"}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Geração */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card rounded-lg border p-4 space-y-3">
          <h4 className="text-sm font-semibold">Movimentação periódica</h4>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label>Início</Label>
              <Input type="date" className="w-40" value={inicio} onChange={(e) => setInicio(e.target.value)} /></div>
            <div className="space-y-1"><Label>Fim</Label>
              <Input type="date" className="w-40" value={fim} onChange={(e) => setFim(e.target.value)} /></div>
            <Button type="button" disabled={enviando}
              onClick={() => enviarMovimentacao({ inicio, fim, validarXsd: true })}>
              <Send className="h-4 w-4 mr-1" /> Gerar e enviar
            </Button>
          </div>
        </div>
        <div className="bg-card rounded-lg border p-4 space-y-3">
          <h4 className="text-sm font-semibold">Inventário</h4>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label>Data de referência</Label>
              <Input type="date" className="w-40" value={dataRef} onChange={(e) => setDataRef(e.target.value)} /></div>
            <Button type="button" disabled={enviando}
              onClick={() => enviarInventario({ dataReferencia: dataRef, validarXsd: true })}>
              <Send className="h-4 w-4 mr-1" /> Gerar e enviar
            </Button>
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">#</TableHead>
              <TableHead className="w-[130px]">Tipo</TableHead>
              <TableHead>Período / Referência</TableHead>
              <TableHead className="w-[160px]">Geração</TableHead>
              <TableHead className="w-[110px]">Status</TableHead>
              <TableHead className="w-[80px]">Canal</TableHead>
              <TableHead className="w-[160px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>}
            {!isLoading && transmissoes.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Nenhuma transmissão gerada ainda.</TableCell></TableRow>
            )}
            {transmissoes.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="font-mono">{t.id}</TableCell>
                <TableCell>{t.tipo}</TableCell>
                <TableCell className="text-sm">
                  {t.dtReferencia
                    ? new Date(t.dtReferencia).toLocaleDateString("pt-BR")
                    : `${t.dtInicioPeriodo ? new Date(t.dtInicioPeriodo).toLocaleDateString("pt-BR") : "?"} → ${t.dtFimPeriodo ? new Date(t.dtFimPeriodo).toLocaleDateString("pt-BR") : "?"}`}
                </TableCell>
                <TableCell className="text-sm">{new Date(t.dtGeracao).toLocaleString("pt-BR")}</TableCell>
                <TableCell>{statusBadge(t.status)}</TableCell>
                <TableCell>{t.canal}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" title="Ver XML" onClick={() => verXml(t.id)}><FileCode className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" title="Reenviar" onClick={() => reenviar(t.id)}><RefreshCw className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" title="Consultar status" onClick={() => consultarStatus(t.id)}><Search className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!xmlAberto} onOpenChange={(o) => !o && setXmlAberto(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>XML da transmissão #{xmlAberto?.id}</DialogTitle></DialogHeader>
          <pre className="text-xs bg-muted p-4 rounded overflow-auto whitespace-pre-wrap break-all">{xmlAberto?.xml}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
